import React, { createContext, useContext, useEffect, useRef, useState, useCallback, ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  NotificationItem, getUnreadCount, getNotifications,
  markNotificationClicked, markNotificationRead, openNotificationStream,
} from '../composables/notificationAPI';
import { readAndClearPendingNotificationTarget } from '../composables/pendingNotificationTarget';
import { useUser } from './UserContext';
import NewNotificationToast from '../components/Notifications/NewNotificationToast';

const NEW_NOTIFICATION_TOAST_MS = 8000;
// query param ที่ backend แนบ id แจ้งเตือนมากับ deepLink ที่ส่งไปช่องทางอื่น (Telegram ฯลฯ)
// เช่น /article/<id>?from-notification=xxx, /realtime?dateKey=...&from-notification=xxx
const NOTIFICATION_ID_QUERY_PARAM = 'from-notification';

type SseStatus = 'idle' | 'connecting' | 'connected' | 'retrying';

interface NotificationContextValue {
  unreadCount: number;
  feedVersion: number;
  sseStatus: SseStatus;
  refreshUnreadCount: () => Promise<void>;
  // เรียกหลังทำ interaction (read/dismiss/...) ในเครื่องนี้เอง เพื่อให้ badge/feed อัปเดตทันที ไม่ต้องรอ SSE
  notifyLocalChange: () => void;
}

const NotificationContext = createContext<NotificationContextValue | undefined>(undefined);

export const NotificationProvider = ({ children }: { children: ReactNode }) => {
  const { userInfo } = useUser();
  // App.tsx มี setInterval เรียก refreshUser(true) ทุก 10 วิเพื่อ sync ข้อมูล user (ดู App.tsx) ซึ่งทุกครั้ง
  // ที่ยิง จะได้ userInfo object ใหม่เอี่ยมจาก API เสมอ (คนละ reference แม้เนื้อหาจะเหมือนเดิมทุกตัวอักษร)
  // ถ้า useEffect ด้านล่าง (เปิด SSE) depend บน userInfo ตรง ๆ จะโดน re-run ทุก ~10 วิไปด้วย ทำให้ปิด SSE
  // เดิมทิ้งแล้วเปิดใหม่ + รีเซ็ต "baseline" (knownTopIdRef ฯลฯ) ทุกรอบ — ถ้าแจ้งเตือนใหม่ดันมาถึงช่วงที่
  // กำลังจะรีเซ็ตพอดี มันจะถูกกลืนเป็น baseline ใหม่เงียบ ๆ แทนที่จะถูกตรวจพบว่า "ใหม่" แล้ว toast เลยขึ้น
  // บ้างไม่ขึ้นบ้างแบบสุ่ม ๆ ตามจังหวะ — แก้โดย derive เป็น id เฉย ๆ (ค่าคงที่ตราบใดที่ยังเป็น user เดิม)
  // มาใช้เป็น dependency แทนทั้ง object
  const userId = userInfo?.id ?? null;
  const navigate = useNavigate();
  // react-router v7 คืน navigate ตัวใหม่ทุกครั้งที่ pathname เปลี่ยน ถ้าใส่ navigate ใน deps ของ callback ตรง ๆ
  // จะลามเป็นทอด navigate → openFromToast → checkForNewNotification → effect SSE ด้านล่าง ทำให้ SSE ถูกปิด/
  // เปิดใหม่ + รีเซ็ต baseline ทุกครั้งที่เปลี่ยนหน้า (อาการเดียวกับบั๊ก userInfo ข้างบน: toast ขึ้นบ้างไม่ขึ้นบ้าง)
  // เลยเก็บไว้ใน ref แล้วเรียกผ่าน navigateRef.current แทน callback จะได้คงที่
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const location = useLocation();
  const [unreadCount, setUnreadCount] = useState(0);
  const [feedVersion, setFeedVersion] = useState(0);
  const [sseStatus, setSseStatus] = useState<SseStatus>('idle');
  const generationRef = useRef(0);
  const knownTopIdRef = useRef<string | null>(null);
  // เก็บ publishedAt (ms) ของแจ้งเตือนบนสุดคู่กับ id — เพราะแจ้งเตือนบางประเภท (เช่น REALTIME_PUBLISHED
  // ของพอร์ตเดียวกันในวันเดียวกัน) backend ใช้ collapseKey แล้ว "อัปเดตทับ" record เดิมแทนสร้างใหม่ ตอน
  // re-publish รอบสอง id จะไม่เปลี่ยนเลย เช็คแค่ id อย่างเดียวเลยพลาด toast (บั๊กที่แอดมินรายงานว่ารอบสอง
  // in-app ไม่เด้ง ทั้งที่ backend ส่งจริง) — ต้องดู publishedAt ขยับขึ้นด้วยถึงจะนับว่า "มีอะไรใหม่"
  const knownTopPublishedAtRef = useRef<number | null>(null);
  const initializedTopIdRef = useRef(false);

  const refreshUnreadCount = useCallback(async (): Promise<void> => {
    try {
      const d = await getUnreadCount();
      setUnreadCount(d.count ?? 0);
    } catch {
      // เน็ตหลุด/ยังไม่ login ก็ปล่อยผ่าน ไม่ต้อง toast ซ้ำ (interaction อื่น ๆ จะ toast เองอยู่แล้ว)
    }
  }, []);

  const notifyLocalChange = useCallback(() => {
    setFeedVersion((v) => v + 1);
    void refreshUnreadCount();
  }, [refreshUnreadCount]);

  // เปิดแจ้งเตือนจาก toast ป๊อปอัพ — บันทึกว่ากด/อ่านแล้ว แล้วพาไปหน้าปลายทาง (ถ้ามี)
  const openFromToast = useCallback((item: NotificationItem) => {
    markNotificationClicked(item.id).catch(() => {});
    markNotificationRead(item.id).catch(() => {});
    notifyLocalChange();
    if (item.deepLink) {
      try {
        const parsed = new URL(item.deepLink, window.location.origin);
        if (parsed.origin === window.location.origin) {
          navigateRef.current(parsed.pathname + parsed.search);
        } else {
          const win = window.open(item.deepLink, '_blank');
          if (win) win.opener = null;
        }
      } catch {
        // deepLink ผิดรูปแบบ — ไม่ navigate
      }
    }
  }, [notifyLocalChange]);

  // เช็คว่ามีแจ้งเตือนใหม่ตั้งแต่ครั้งก่อนมั้ย ถ้ามีก็โชว์ toast มุมขวาบนสัก 5-8 วิ
  // ครั้งแรกที่ล็อกอิน/โหลดหน้า แค่จดจำ id บนสุดไว้เฉย ๆ ไม่ toast (กันเด้งย้อนหลังทุกครั้งที่เปิดแอป)
  //
  // คุยกับ backend แล้ว (2026-10) — เปลี่ยน contract ของ event "notification-awareness":
  //   - { refresh: true }                        = แค่เลขแดงเปลี่ยน (อ่าน/ลบที่อุปกรณ์อื่น ฯลฯ) ไม่ใช่
  //                                                 แจ้งเตือนใหม่ → อัปเดต unreadCount เฉย ๆ พอ ไม่ต้องยิง
  //                                                 /notifications?limit=1 ซ้ำ (เดิมยิงทุกครั้งรวมถึง
  //                                                 heartbeat ที่ส่งมาทุก ~30s ด้วย backend เลยขอให้ลด)
  //   - { refresh: true, notificationId: 'xxx' } = backend ยืนยันแล้วว่ามีแจ้งเตือนใหม่ของเราจริง ๆ (fan-out
  //                                                 เสร็จพร้อมให้ query ได้แล้ว) → ค่อยยิง checkForNewNotification
  // ด้วย contract ใหม่นี้ไม่ต้องเดา/รอ fan-out แบบเดิมอีกแล้ว (ตัดปัญหา race ที่เคยแก้ด้วย retry/เทียบ
  // unread count tier ยาว ๆ ทิ้งไปได้) เหลือ retry สั้น ๆ ไว้เป็น safety net เผื่อ edge case เล็กน้อยเท่านั้น
  const RETRY_DELAYS_MS = [800, 1500];
  const checkForNewNotification = useCallback(async (attempt = 0): Promise<void> => {
    try {
      const { items } = await getNotifications({ limit: 1 });
      const top = items[0];
      if (!top) return;
      const topPublishedAtMs = top.publishedAt ? new Date(top.publishedAt).getTime() : null;
      if (!initializedTopIdRef.current) {
        knownTopIdRef.current = top.id;
        knownTopPublishedAtRef.current = topPublishedAtMs;
        initializedTopIdRef.current = true;
        return;
      }
      // "มีอะไรใหม่" ถ้า id เปลี่ยน (แจ้งเตือนรายการใหม่) หรือ id เดิมแต่ publishedAt ขยับขึ้น (โดน
      // re-publish ทับ record เดิมผ่าน collapseKey — ดูคอมเมนต์ที่ knownTopPublishedAtRef ด้านบน)
      const idChanged = top.id !== knownTopIdRef.current;
      const republished = !idChanged
        && topPublishedAtMs !== null
        && knownTopPublishedAtRef.current !== null
        && topPublishedAtMs > knownTopPublishedAtRef.current;
      if (!idChanged && !republished) {
        const delay = RETRY_DELAYS_MS[attempt];
        if (delay !== undefined) {
          setTimeout(() => { void checkForNewNotification(attempt + 1); }, delay);
        }
        return;
      }
      knownTopIdRef.current = top.id;
      knownTopPublishedAtRef.current = topPublishedAtMs;
      toast.custom(
        (t) => (
          <NewNotificationToast
            item={top}
            visible={t.visible}
            onClick={() => { openFromToast(top); toast.dismiss(t.id); }}
            onDismiss={() => toast.dismiss(t.id)}
          />
        ),
        { duration: NEW_NOTIFICATION_TOAST_MS, position: 'top-right' }
      );
    } catch {
      // fetch ไม่สำเร็จ — ไม่ต้อง toast error ซ้ำ เดี๋ยว SSE hint รอบหน้าค่อยลองใหม่
    }
  }, [openFromToast]);

  // แท็บแอปที่เปิดอยู่รับ postMessage จาก sw.js ตอนกดคลิก push notification
  // เพื่อบันทึก click + พาไปหน้าปลายทางแบบ client-side (ไม่ reload ทั้งหน้า)
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    const onMessage = (event: MessageEvent) => {
      if (event.data?.type !== 'NOTIFICATION_CLICK') return;
      const { id, url } = event.data as { id?: string; url?: string };
      if (id) {
        markNotificationClicked(id).catch(() => {}).finally(notifyLocalChange);
      }
      if (url) {
        try {
          const path = new URL(url).pathname + new URL(url).search;
          navigateRef.current(path);
        } catch {
          // url ผิดรูปแบบ ก็แค่ไม่ navigate
        }
      }
    };
    navigator.serviceWorker.addEventListener('message', onMessage);
    return () => navigator.serviceWorker.removeEventListener('message', onMessage);
  }, [notifyLocalChange]);

  // ตอนแอปเปิดขึ้นมา (เช่น เพิ่งถูกเปิดจากการกด push notification บน lock screen ตอนแอปโดนปิดไปแล้ว)
  // เช็คว่ามีปลายทางค้างอยู่ใน IndexedDB มั้ย (sw.js เขียนไว้กัน iOS เมิน url ตอน openWindow) แล้ว navigate ไปเอง
  useEffect(() => {
    (async () => {
      const pending = await readAndClearPendingNotificationTarget();
      if (!pending) return;
      if (pending.id) markNotificationClicked(pending.id).catch(() => {}).finally(notifyLocalChange);
      try {
        const parsed = new URL(pending.url, window.location.origin);
        navigate(parsed.pathname + parsed.search);
      } catch {
        // url ผิดรูปแบบ ก็แค่ไม่ navigate
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // เปิดแอปจาก deepLink ช่องทางอื่น (Telegram ฯลฯ) ที่แนบ ?from-notification=xxx มา — ถือว่าผู้ใช้อ่านแจ้งเตือนนั้นแล้ว
  // (ใช้ได้ทุกหน้า: บทความ, มุมมองซื้อ-ขาย /realtime ฯลฯ) รอจน login ก่อนค่อยยิง (API ต้องใช้ token)
  // แล้วลบ param ออกจาก URL (param อื่นอย่าง dateKey/realtimeId คงไว้) กันยิงซ้ำตอน refresh/แชร์ลิงก์ต่อ
  // handledDeepLinkIdsRef กันยิงซ้ำตอน effect re-run (StrictMode / location เปลี่ยนก่อน replace เสร็จ)
  const handledDeepLinkIdsRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    // TODO DEBUG: ลบ log กลุ่ม [NotifDebug] ทิ้งหลังเช็คว่าลิงก์จาก Telegram แล้ว mark read ได้จริง
    console.log('[NotifDebug] deepLink check', { userId, search: location.search, href: window.location.href });
    if (!userId) return;
    const params = new URLSearchParams(location.search);
    const id = params.get(NOTIFICATION_ID_QUERY_PARAM);
    if (!id) return;
    params.delete(NOTIFICATION_ID_QUERY_PARAM);
    const search = params.toString();
    navigateRef.current(
      { pathname: location.pathname, search: search ? `?${search}` : '', hash: location.hash },
      { replace: true, state: location.state }
    );
    if (handledDeepLinkIdsRef.current.has(id)) return;
    handledDeepLinkIdsRef.current.add(id);
    markNotificationRead(id)
      .then((res) => console.log('[NotifDebug] deepLink mark read OK', id, res))
      .catch((err) => console.log('[NotifDebug] deepLink mark read FAILED', id, err))
      .finally(notifyLocalChange);
  }, [userId, location.search, location.pathname, location.hash, location.state, notifyLocalChange]);

  // SSE เป็น fetch+stream แทน EventSource เพราะ backend auth ด้วย Bearer header (EventSource ใส่ header เองไม่ได้)
  useEffect(() => {
    const loggedIn = !!userInfo && !!localStorage.getItem('token');
    if (!loggedIn) {
      setUnreadCount(0);
      setSseStatus('idle');
      knownTopIdRef.current = null;
      knownTopPublishedAtRef.current = null;
      initializedTopIdRef.current = false;
      return;
    }

    generationRef.current += 1;
    const myGeneration = generationRef.current;
    const ac = new AbortController();
    let backoff = 1000;

    // TODO DEBUG: ลบ log กลุ่ม [NotifDebug] ทิ้งหลังทดสอบทฤษฎี "SSE effect รีเซ็ตทุก ~10s" เสร็จแล้ว —
    // ของปกติ log นี้ควรขึ้นแค่ "ครั้งเดียว" ตอนเข้าแอป/ล็อกอิน ไม่ใช่ขึ้นรัว ๆ ทุก 10 วิ ถ้าขึ้นรัว ๆ
    // แปลว่ายังมีอะไรบางอย่างทำให้ effect นี้ re-run อยู่ (เดิมคือ userInfo object เปลี่ยน reference ทุก
    // 10s จาก setInterval(refreshUser) ใน App.tsx — แก้ไปแล้วโดยเปลี่ยน dependency เป็น userId)
    console.log('[NotifDebug] SSE effect (RE)SETUP — generation', myGeneration, { time: new Date().toISOString(), userId });

    // เซสชันใหม่ (ล็อกอินใหม่/สลับ user) — เริ่มจดจำ id บนสุดใหม่ ไม่เอาของ user ก่อนหน้ามาปน
    knownTopIdRef.current = null;
    knownTopPublishedAtRef.current = null;
    initializedTopIdRef.current = false;
    void refreshUnreadCount();
    void checkForNewNotification();

    async function run() {
      while (myGeneration === generationRef.current) {
        setSseStatus('connecting');
        try {
          const res = await openNotificationStream(ac.signal);
          if (!res.ok || !res.body) throw new Error('SSE HTTP ' + res.status);
          setSseStatus('connected');
          backoff = 1000;
          const reader = res.body.getReader();
          const decoder = new TextDecoder();
          let buffer = '';
          while (myGeneration === generationRef.current) {
            const { value, done } = await reader.read();
            if (done) break;
            buffer += decoder.decode(value, { stream: true });
            let sep;
            while ((sep = /\r?\n\r?\n/.exec(buffer))) {
              const block = buffer.slice(0, sep.index);
              buffer = buffer.slice(sep.index + sep[0].length);
              const lines = block.split(/\r?\n/);
              const event = lines.find((l) => l.startsWith('event:'))?.slice(6).trim();
              const data = lines.filter((l) => l.startsWith('data:')).map((l) => l.slice(5).trimStart()).join('\n');
              if (event === 'notification-awareness' && data) {
                try {
                  const parsed = JSON.parse(data);
                  if (parsed.refresh) {
                    setFeedVersion((v) => v + 1);
                    void refreshUnreadCount();
                    // มี notificationId มาด้วย = backend ยืนยันแล้วว่ามีแจ้งเตือนใหม่ของเราจริง พร้อม query
                    // ได้แล้ว (ดู contract ที่คอมเมนต์ checkForNewNotification ด้านบน) — ถ้าเป็นแค่
                    // { refresh: true } เฉย ๆ (เช่น heartbeat ทุก ~30s หรืออ่าน/ลบที่อุปกรณ์อื่น) ไม่ต้องยิง
                    // /notifications?limit=1 ซ้ำ อัปเดตแค่เลขแดงพอ (ลดโหลด backend ตามที่ขอมา)
                    if (parsed.notificationId) {
                      void checkForNewNotification();
                    }
                  }
                } catch {
                  // ignore malformed frame
                }
              }
            }
            if (buffer.length > 1_000_000) throw new Error('SSE frame ใหญ่เกินกำหนด');
          }
        } catch (err: any) {
          if (err?.name === 'AbortError') return;
        }
        if (myGeneration !== generationRef.current) return;
        setSseStatus('retrying');
        const currentBackoff = backoff;
        await new Promise((resolve) => setTimeout(resolve, currentBackoff));
        backoff = Math.min(backoff * 2, 30000);
      }
    }
    void run();

    return () => {
      // TODO DEBUG: ลบ log กลุ่ม [NotifDebug] ทิ้งหลังทดสอบเสร็จ
      console.log('[NotifDebug] SSE effect CLEANUP (tearing down) — generation', myGeneration, { time: new Date().toISOString() });
      generationRef.current += 1;
      ac.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, refreshUnreadCount, checkForNewNotification]);

  return (
    <NotificationContext.Provider value={{ unreadCount, feedVersion, sseStatus, refreshUnreadCount, notifyLocalChange }}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const ctx = useContext(NotificationContext);
  if (!ctx) throw new Error('useNotifications must be used within a NotificationProvider');
  return ctx;
};
